import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Upload, X, Trash2, Plus, Tag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NotesManager({ user, isOpen, onClose }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    tags: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchNotes();
    }
  }, [isOpen]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/notes`);
      setNotes(response.data.notes || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Failed to load notes');
    }
  };

  const handleUploadNote = async () => {
    if (!newNote.title.trim() || !newNote.content.trim()) {
      toast.error('Please provide both title and content');
      return;
    }

    setLoading(true);
    try {
      const tags = newNote.tags.split(',').map(t => t.trim()).filter(t => t);
      
      await axios.post(`${API}/notes/upload`, {
        title: newNote.title,
        content: newNote.content,
        tags: tags
      });

      toast.success('Note uploaded successfully!');
      setNewNote({ title: '', content: '', tags: '' });
      setShowUploadForm(false);
      fetchNotes();
    } catch (error) {
      console.error('Error uploading note:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload note';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    try {
      await axios.delete(`${API}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setNewNote(prev => ({ ...prev, content: text }));
      toast.success('Content pasted from clipboard');
    } catch (error) {
      toast.error('Failed to paste from clipboard');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="w-full max-w-4xl my-8 border-2 border-pulpit-200">
        <CardHeader className="bg-gradient-to-r from-pulpit-100 to-glory-50 border-b">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl text-pulpit-900 flex items-center">
                <FileText className="w-6 h-6 mr-2" />
                My Sermon Notes
              </CardTitle>
              <CardDescription className="mt-2">
                Upload and manage your existing notes to enhance AI sermon generation
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Upload Section */}
          {!showUploadForm ? (
            <div className="mb-6">
              <Button
                onClick={() => setShowUploadForm(true)}
                className="bg-pulpit-600 hover:bg-pulpit-700 w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload New Note
              </Button>
            </div>
          ) : (
            <Card className="mb-6 border-2 border-glory-300 bg-glory-50">
              <CardContent className="p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-pulpit-900">Upload Note</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowUploadForm(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div>
                  <Label htmlFor="note-title">Note Title</Label>
                  <Input
                    id="note-title"
                    value={newNote.title}
                    onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                    placeholder="e.g., Grace Sermon Ideas"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="note-content">Content</Label>
                  <div className="flex gap-2 mt-1 mb-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handlePasteFromClipboard}
                      className="text-xs"
                    >
                      📋 Paste from Clipboard
                    </Button>
                  </div>
                  <Textarea
                    id="note-content"
                    value={newNote.content}
                    onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                    placeholder="Paste your sermon notes, ideas, or research here..."
                    className="min-h-[200px]"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Copy and paste notes from your Word docs, Google Docs, or other files
                  </p>
                </div>

                <div>
                  <Label htmlFor="note-tags">Tags (optional)</Label>
                  <Input
                    id="note-tags"
                    value={newNote.tags}
                    onChange={(e) => setNewNote({ ...newNote, tags: e.target.value })}
                    placeholder="e.g., grace, faith, new testament (comma-separated)"
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleUploadNote}
                    disabled={loading}
                    className="bg-pulpit-600 hover:bg-pulpit-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {loading ? 'Uploading...' : 'Upload Note'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowUploadForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes List */}
          <div>
            <h4 className="font-semibold text-pulpit-900 mb-3">
              Your Notes ({notes.length})
            </h4>

            {notes.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-2">No notes uploaded yet</p>
                <p className="text-sm text-gray-500">
                  Upload your existing sermon notes to enhance AI generation
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {notes.map((note) => (
                  <Card key={note.id} className="border border-gray-200 hover:border-pulpit-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h5 className="font-semibold text-pulpit-900 mb-1">
                            {note.title}
                          </h5>
                          <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                            {note.content}
                          </p>
                          {note.tags && note.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {note.tags.map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs bg-pulpit-100 text-pulpit-700 px-2 py-1 rounded-full"
                                >
                                  <Tag className="w-3 h-3 inline mr-1" />
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            Added: {new Date(note.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
